import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, MapPin, Calendar, DollarSign, Mail, Phone, AlertCircle, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import apiService from '@/services/api';

interface PaymentDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  paymentId: number | null;
}

interface PaymentDetail {
  payment_id: number;
  amount_gbp: string;
  amount_pence: number;
  status: string;
  payment_date: string;
  paid_at: string;
  payment_method: string;
  stripe_payment_intent_id: string;
  customer: {
    id: number;
    name: string;
    email: string;
    phone: string;
    address: string;
  };
  booking: {
    id: number;
    status: string;
    booking_date: string;
    booking_time: string;
    notes: string;
    total_gbp: string;
  };
  service: {
    id: number;
    name: string;
    description: string;
  };
  provider: {
    name: string;
    email: string;
    phone: string;
  };
}

const PaymentDetailsModal: React.FC<PaymentDetailsModalProps> = ({ isOpen, onClose, paymentId }) => {
  const [payment, setPayment] = useState<PaymentDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && paymentId) {
      console.log(`🔍 Modal opened for payment: ${paymentId}`);
      loadPaymentDetails();
    }
  }, [isOpen, paymentId]);

  const loadPaymentDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log(`📡 Fetching details for payment ${paymentId}...`);
      const response = await apiService.get(`/admin/payment/${paymentId}`);
      console.log('✓ Payment details loaded:', response);
      if (response.success && response.data) {
        setPayment(response.data);
        console.log(`✓ Payment ${paymentId} amount: $${response.data.amount_gbp}`);
      } else {
        setError('Failed to load payment details');
        console.error('❌ API returned success=false');
      }
    } catch (err) {
      console.error('❌ Error loading payment details:', err);
      setError('Failed to load payment details');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between pb-2 border-b">
          <CardTitle>Payment Details</CardTitle>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition"
          >
            <X className="h-5 w-5" />
          </button>
        </CardHeader>

        <CardContent className="space-y-6 pt-6">
          {loading && (
            <div className="text-center py-8">
              <p className="text-gray-600">Loading payment details...</p>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex gap-2">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {payment && (
            <>
              {/* Payment Header */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Payment ID</p>
                  <p className="text-lg font-bold text-gray-900">#{payment.payment_id}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Amount Received</p>
                  <p className="text-lg font-bold text-green-600">£{payment.amount_gbp}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Status</p>
                  <div className="flex items-center gap-2">
                    {payment.status === 'succeeded' ? (
                      <>
                        <CheckCircle className="h-5 w-5 text-green-600" />
                        <span className="font-semibold text-green-600 capitalize">{payment.status}</span>
                      </>
                    ) : (
                      <span className="font-semibold text-gray-600 capitalize">{payment.status}</span>
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Payment Method</p>
                  <p className="font-semibold text-gray-900 capitalize">{payment.payment_method}</p>
                </div>
              </div>

              <hr />

              {/* Customer Information */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Customer Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Name</p>
                    <p className="font-medium text-gray-900">{payment.customer.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Email</p>
                    <div className="flex items-center gap-2 text-gray-900">
                      <Mail className="h-4 w-4 text-gray-500" />
                      <a href={`mailto:${payment.customer.email}`} className="text-blue-600 hover:underline">
                        {payment.customer.email}
                      </a>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Phone</p>
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-gray-500" />
                      <p className="text-gray-900">{payment.customer.phone || 'N/A'}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Address</p>
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-gray-500" />
                      <p className="text-gray-900">{payment.customer.address || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              </div>

              <hr />

              {/* Booking/Quote Information */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Booking Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Booking ID</p>
                    <p className="font-medium text-gray-900">#{payment.booking.id}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Booking Status</p>
                    <p className="font-medium text-gray-900 capitalize">{payment.booking.status}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Booking Date</p>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-gray-500" />
                      <p className="text-gray-900">
                        {payment.booking.booking_date ? new Date(payment.booking.booking_date).toLocaleDateString() : 'N/A'}
                      </p>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Booking Time</p>
                    <p className="text-gray-900">
                      {payment.booking.booking_time || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Booking Total</p>
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-gray-500" />
                      <p className="font-semibold text-gray-900">£{payment.booking.total_gbp}</p>
                    </div>
                  </div>
                </div>
                {payment.booking.notes && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">Notes</p>
                    <p className="text-gray-900">{payment.booking.notes}</p>
                  </div>
                )}
              </div>

              <hr />

              {/* Service Information */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Service Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Service</p>
                    <p className="font-medium text-gray-900">{payment.service.name}</p>
                  </div>
                </div>
                {payment.service.description && (
                  <div className="mt-3">
                    <p className="text-sm text-gray-600 mb-1">Description</p>
                    <p className="text-gray-900">{payment.service.description}</p>
                  </div>
                )}
              </div>

              <hr />

              {/* Provider Information */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Provider Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Provider</p>
                    <p className="font-medium text-gray-900">{payment.provider.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Email</p>
                    <a href={`mailto:${payment.provider.email}`} className="text-blue-600 hover:underline">
                      {payment.provider.email}
                    </a>
                  </div>
                </div>
              </div>

              <hr />

              {/* Transaction Information */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Transaction Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Payment Date</p>
                    <p className="text-gray-900">
                      {new Date(payment.payment_date).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Paid At</p>
                    <p className="text-gray-900">
                      {payment.paid_at ? new Date(payment.paid_at).toLocaleString() : 'N/A'}
                    </p>
                  </div>
                  {payment.stripe_payment_intent_id && (
                    <div className="col-span-2">
                      <p className="text-sm text-gray-600 mb-1">Stripe Payment Intent ID</p>
                      <p className="font-mono text-xs text-gray-600 break-all bg-gray-50 p-2 rounded">
                        {payment.stripe_payment_intent_id}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {!loading && !error && (
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default PaymentDetailsModal;
